import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Camera, X } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../context/AuthContext";
// import { toast } from "sonner";
import { useLocation, useNavigate } from "react-router-dom";
import useProfileUpdate from "../hooks/useProfileUpdate";
import { HashLoader } from "react-spinners";
import { shortenFileName } from "../helpers/files";
import doneImg from "../assets/images/successfully-done.svg";
import { leaveSetupTask } from "../utils/setupTaskReturn";

const UploadProfilePictureModal: FC<{}> = () => {
  const { refetch, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const fromSetup = (location.state as { fromSetup?: boolean } | null)?.fromSetup;

  const [completed, setCompleted] = useState(false);
  const [profileImg, setProfileImg] = useState<File | null>(null);

  const [showCameraModal, setShowCameraModal] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [cameraReady, setCameraReady] = useState(false);

  const {
    updateProfile,
    updatingProfile,
    isProfileUpdateError,
    profileUpdateError,
  } = useProfileUpdate();

  // Use existing user data mapped safely to bypass TS definition errors
  const data = useMemo(() => {
    return {
      firstname: user?.firstname,
      lastname: user?.lastname,
      phone: user?.phone,
      address: (user as any)?.address || "",
    };
  }, [user]);

  const imageRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const handleSubmit = useCallback(() => {
    if (profileImg) {
      updateProfile(
        {
          ...data,
          profile_pics: {
            file: profileImg,
            name: profileImg.name,
          },
        } as any, // Bypass strict type mapping for profile updating
        {
          onSuccess() {
            setCompleted(true);
            refetch();
          },
        }
      );
    }
  }, [profileImg, updateProfile, data, refetch]);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, []);

  const closeCamera = useCallback(() => {
    stopCamera();
    setShowCameraModal(false);
    setCameraReady(false);
  }, [stopCamera]);

  useEffect(() => {
    if (!showCameraModal) return;

    let cancelled = false;
    setCameraError(null);
    setCameraReady(false);

    const startCamera = async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          setCameraError(
            "Camera capture isn’t supported in this browser. Please use upload instead."
          );
          return;
        }
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user" },
          audio: false,
        });

        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
      } catch (err) {
        setCameraError(
          "We couldn’t access your camera. Please allow camera permissions or use upload instead."
        );
      }
    };

    startCamera();

    return () => {
      cancelled = true;
      stopCamera();
    };
  }, [showCameraModal, stopCamera]);

  const handleCapture = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    const width = video.videoWidth || 720;
    const height = video.videoHeight || 720;
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.drawImage(video, 0, 0, width, height);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const file = new File([blob], `selfie-${Date.now()}.jpg`, {
          type: "image/jpeg",
        });
        setProfileImg(file);
        closeCamera();
      },
      "image/jpeg",
      0.92
    );
  }, [closeCamera]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed top-0 left-0 bg-deepBlue/80 backdrop-blur-sm w-full h-screen grid place-items-center p-5 z-[9999]"
    >
      <canvas ref={canvasRef} className="hidden" />

      {updatingProfile && (
        <div className="absolute top-0 z-[10005] left-0 h-screen w-screen flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <HashLoader />
        </div>
      )}

      {/* Done Screen overlay inside modal directly */}
      {completed ? (
        <motion.div
           initial={{ opacity: 0, scale: 0.95 }}
           animate={{ opacity: 1, scale: 1 }}
           className="bg-white max-w-sm w-full p-8 rounded-2xl shadow-sm flex items-center flex-col gap-6"
        >
          <img src={doneImg} alt="done" className="w-24 h-24 object-contain" />
          <div className="flex flex-col gap-4 text-center">
            <div className="text-2xl font-bold">Successfully Uploaded!</div>
            <div className="text-gray-500 font-medium pb-2 text-sm">Your profile picture has been saved.</div>
            <button
              onClick={() => {
                if (fromSetup) {
                  leaveSetupTask(navigate, location.state);
                } else {
                  navigate(-1);
                }
              }}
              className="p-3 w-full border-visaro-gray-700 border-2 rounded-lg font-bold bg-deepBlue text-white text-sm"
            >
              Continue
            </button>
          </div>
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 30 }}
          className="bg-white max-w-sm w-full p-5 rounded-2xl shadow-sm flex items-center flex-col gap-5 relative"
        >
          <div className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 cursor-pointer">
             <X size={24} onClick={() => navigate(-1)} />
          </div>
          
          <div className="flex items-center flex-col gap-2 mt-2">
            <div className="font-medium text-xl">Profile Picture</div>
            <p className="text-gray-500 text-center text-sm px-4">
              A clear selfie helps us keep your account secure.
            </p>
          </div>
          
          <div className="w-full flex-col gap-6 flex items-center justify-center mt-2">
            <div
              onClick={() => setShowCameraModal(true)}
              className="border w-full max-w-[14rem] rounded-full aspect-square relative overflow-hidden border-dashed border-gray-300 p-6 flex items-center justify-center flex-col gap-3 hover:bg-gray-50 cursor-pointer transition"
            >
              {profileImg ? (
                <div className="rounded-full w-full h-full bg-white absolute top-0 left-0">
                  <img
                    draggable={false}
                    src={URL.createObjectURL(profileImg)}
                    alt="preview"
                    className=" rounded-full w-full object-cover object-center h-full"
                  />
                </div>
              ) : (
                <>
                  <div className="bg-gray-100 text-gray-700 p-4 rounded-full">
                    <Camera size={40} />
                  </div>
                  <div className="text-center font-medium text-sm">Selfie Photo</div>
                </>
              )}
            </div>

            <div className="flex flex-col gap-2 items-center w-full">
              <input
                type="file"
                hidden
                ref={imageRef}
                name="profile_img"
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    setProfileImg(e.target.files[0]);
                  }
                }}
                id="profile_img"
                capture="user"
                accept="image/png, image/jpeg, image/jpg"
              />

              {profileImg && (
                <div className="text-center text-gray-500 text-xs truncate max-w-[200px] mb-2">
                   {shortenFileName(profileImg.name)}
                </div>
              )}

              <div className="flex items-center gap-4">
                {profileImg ? (
                  <button
                    type="button"
                    onClick={() => {
                      setProfileImg(null);
                      setShowCameraModal(true);
                    }}
                    className="text-sm text-gray-700 underline font-medium"
                  >
                    Retake Photo
                  </button>
                ) : (
                  <span className="font-medium text-sm text-gray-950 underline cursor-pointer" onClick={() => setShowCameraModal(true)}>
                    Tap to open camera
                  </span>
                )}
                
                <button
                  type="button"
                  onClick={() => imageRef.current?.click()}
                  className="text-sm text-gray-600 underline"
                >
                  Upload manual
                </button>
              </div>

              {isProfileUpdateError && (
                <div className="text-red-500 text-sm mt-2">{profileUpdateError?.message}</div>
              )}

              <div className="flex items-center gap-4 w-full mt-4">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="p-3 px-5 flex-1 border-red-100 border-2 rounded-lg font-bold bg-red-50/50 text-red-500 text-sm"
                >
                  Cancel
                </button>
                <button
                  disabled={!profileImg || updatingProfile}
                  onClick={handleSubmit}
                  className="p-3 px-5 flex-1 border-visaro-gray-700 border-2 rounded-lg font-bold bg-deepBlue text-white text-sm disabled:opacity-50"
                >
                  Submit
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Internal Camera Capture Overlay */}
      <AnimatePresence>
        {showCameraModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed top-0 left-0 w-full h-screen z-[10001] bg-black/95 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              className="bg-zinc-900 w-full max-w-md rounded-2xl shadow-lg p-6 flex flex-col gap-4 relative"
            >
              <button
                className="absolute top-3 right-3 text-gray-400 hover:text-gray-200"
                onClick={closeCamera}
              >
                <X size={22} weight="bold" />
              </button>

              <div className="text-center text-white mt-2">
                <h2 className="text-xl font-semibold">Take a selfie</h2>
                <p className="text-sm text-gray-400">
                  Make sure your face is well lit and centered.
                </p>
              </div>

              <div className="w-full aspect-square rounded-xl overflow-hidden bg-black mt-2">
                <video
                  ref={videoRef}
                  className="w-full h-full object-cover scale-x-[-1]"
                  playsInline
                  muted
                  onCanPlay={() => setCameraReady(true)}
                />
              </div>

              {cameraError && (
                <div className="text-red-400 text-sm bg-red-900/30 border border-red-500/50 rounded-md p-3 mt-2">
                  {cameraError}
                </div>
              )}

              <div className="flex gap-3 mt-4">
                <button
                  onClick={handleCapture}
                  disabled={!cameraReady}
                  className="flex-1 bg-deepBlue text-white rounded-lg py-3 font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-deepBlue/90"
                >
                  Capture
                </button>
                <button
                  onClick={closeCamera}
                  className="flex-1 border border-zinc-700 rounded-lg py-3 font-semibold text-zinc-300 hover:bg-zinc-800"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default UploadProfilePictureModal;
